import {
  addCubaCalendarDays,
  cubaMinutesOfDay,
  cubaWeekStartDayKey,
  getCubaTimeParts,
} from '../time/cubaTime';
import { type PlayTurn } from './playTurns';
import {
  compareTurnsNewestFirst,
  officialResultsForHouse,
  turnKey,
  type DrawSessionLike,
  type OfficialDrawResult,
  type ResultLike,
} from './officialDrawResults';
import {
  addMinutesToTime,
  getActiveWindow,
  isInHalfOpenTimeRange,
  isInResultsWaitBlock,
  parseTimeToMinutes,
  resolveResultsFocusDayKey,
  resolveWindowSchedule,
  type WindowScheduleConfig,
} from './windowSchedule';

export type {
  DrawSessionLike,
  OfficialDrawResult,
  ResultLike,
} from './officialDrawResults';
export {
  officialResultsForHouse,
  resultTurnOf,
} from './officialDrawResults';

/**
 * Floor for "this week" on the Results board: Cuba week Monday, but never
 * later than yesterday so overnight Monday still lists Sunday draws.
 */
export function resultsHistoryFloorDayKey(nowUtc: Date = new Date()): string {
  const weekStart = cubaWeekStartDayKey(nowUtc);
  const cuba = getCubaTimeParts(nowUtc);
  const prev = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, -1);
  const yesterday = `${prev.year}-${String(prev.month).padStart(2, '0')}-${String(prev.day).padStart(2, '0')}`;
  return weekStart <= yesterday ? weekStart : yesterday;
}

export interface ResultTurnGroup {
  turn: PlayTurn;
  labelKey: 'playsTurnDay' | 'playsTurnNight';
  dayKey: string;
  result: OfficialDrawResult | null;
  /**
   * Hoy strip: active = current focus; upcoming = next turn not started yet (muted);
   * past = earlier turn of the same focus day (show result or overdue).
   */
  liveState: 'active' | 'upcoming' | 'past';
}

export interface PartitionedResults {
  /** Acceptance or results-wait turn currently in focus (pinned at top). */
  currentTurn: PlayTurn | null;
  /** Live strip: only the focused turn (awaiting or just published while still in cycle). */
  todayGroups: ResultTurnGroup[];
  /** Published draws this Cuba week that are not the live focus. */
  historyGroups: ResultTurnGroup[];
  /** Before this week's Monday. */
  archivedGroups: ResultTurnGroup[];
}

/** One calendar day in week/archive history — up to day + night turns. */
export interface ResultDayHistoryGroup {
  dayKey: string;
  day: ResultTurnGroup | null;
  night: ResultTurnGroup | null;
}

/**
 * Collapse per-turn history rows into one card per Cuba day (newest first).
 * Days with no published result on either window are omitted.
 */
export function groupResultTurnsByDay(
  groups: readonly ResultTurnGroup[],
): ResultDayHistoryGroup[] {
  const byDay = new Map<string, ResultDayHistoryGroup>();
  for (const group of groups) {
    if (!group.result) continue;
    const existing = byDay.get(group.dayKey) ?? {
      dayKey: group.dayKey,
      day: null,
      night: null,
    };
    if (group.turn.window === 'night') existing.night = group;
    else existing.day = group;
    byDay.set(group.dayKey, existing);
  }
  return [...byDay.values()].sort((a, b) => (a.dayKey < b.dayKey ? 1 : a.dayKey > b.dayKey ? -1 : 0));
}

/**
 * Window that owns the Results "Hoy" strip.
 * Acceptance stops at close−cutoff, but the wait block only starts at close —
 * without this tail (and the 1-minute reopen gap) Hoy goes blank after a close.
 */
function resolveResultsFocusWindow(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date,
): 'day' | 'night' | null {
  const active = getActiveWindow(config, nowUtc);
  if (active) return active;

  const waiting = isInResultsWaitBlock(config, nowUtc);
  if (waiting) return waiting;

  const schedule = resolveWindowSchedule(config);
  const cur = cubaMinutesOfDay(nowUtc);

  const dayClose = parseTimeToMinutes(schedule.dayClose);
  const nightClose = parseTimeToMinutes(schedule.nightClose);
  const dayAcceptEnd = parseTimeToMinutes(
    addMinutesToTime(schedule.dayClose, -schedule.cutoffMin),
  );
  const nightAcceptEnd = parseTimeToMinutes(
    addMinutesToTime(schedule.nightClose, -schedule.cutoffMin),
  );
  if (isInHalfOpenTimeRange(cur, dayAcceptEnd, dayClose)) return 'day';
  if (isInHalfOpenTimeRange(cur, nightAcceptEnd, nightClose)) return 'night';

  const nightOpen = parseTimeToMinutes(schedule.nightOpen);
  const dayOpen = parseTimeToMinutes(schedule.dayOpen);
  if (isInHalfOpenTimeRange(cur, parseTimeToMinutes(schedule.dayBlock.to), nightOpen)) {
    return 'day';
  }
  if (isInHalfOpenTimeRange(cur, parseTimeToMinutes(schedule.nightBlock.to), dayOpen)) {
    return 'night';
  }
  return null;
}

/**
 * Turn currently in focus on the Results board:
 * open acceptance, cutoff tail, results-wait block, or reopen gap.
 * dayKey uses publish calendar day (overnight day → today after midnight).
 */
export function getResultsFocusTurn(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): PlayTurn | null {
  const window = resolveResultsFocusWindow(config, nowUtc);
  if (!window) return null;
  return {
    dayKey: resolveResultsFocusDayKey(config, window, nowUtc),
    window,
  };
}

function toGroups(
  map: Map<string, { turn: PlayTurn; result: OfficialDrawResult | null; liveState: ResultTurnGroup['liveState'] }>,
  opts: { chronologicalToday?: boolean } = {},
): ResultTurnGroup[] {
  return [...map.values()]
    .sort((a, b) => {
      if (opts.chronologicalToday && a.turn.dayKey === b.turn.dayKey && a.turn.window !== b.turn.window) {
        return a.turn.window === 'day' ? -1 : 1;
      }
      if (a.turn.dayKey === b.turn.dayKey && a.turn.window !== b.turn.window) {
        return a.turn.window === 'day' ? -1 : 1;
      }
      return compareTurnsNewestFirst(a.turn, b.turn);
    })
    .map(({ turn, result, liveState }) => ({
      turn,
      dayKey: turn.dayKey,
      labelKey: turn.window === 'night' ? 'playsTurnNight' as const : 'playsTurnDay' as const,
      result,
      liveState,
    }));
}

function findExactOfficial(
  official: OfficialDrawResult[],
  dayKey: string,
  window: PlayTurn['window'],
): OfficialDrawResult | null {
  return official.find((row) => row.dayKey === dayKey && row.window === window) ?? null;
}

function findResultForFocus(
  official: OfficialDrawResult[],
  focus: PlayTurn,
): OfficialDrawResult | null {
  const exact = findExactOfficial(official, focus.dayKey, focus.window);
  if (exact) return exact;

  const focusMs = Date.parse(`${focus.dayKey}T12:00:00Z`);
  let best: OfficialDrawResult | null = null;
  let bestDist = Infinity;
  for (const row of official) {
    if (row.window !== focus.window) continue;
    // Never attach an older business day to an open focus turn.
    if (row.dayKey < focus.dayKey) continue;
    const dist = Math.abs(Date.parse(`${row.dayKey}T12:00:00Z`) - focusMs);
    // Overnight acceptance dayKey vs Eastern publish dayKey (+1 calendar day).
    if (dist <= 36 * 3_600_000 && dist < bestDist) {
      best = row;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Split official results into live focus / week history / archived.
 *
 * Hoy always lists both Día and Noche for the focus business day: the active
 * turn stays interactive; the next turn is muted until its window arrives.
 */
export function partitionResultsByTurn(
  results: ResultLike[],
  drawSessions: DrawSessionLike[] | undefined,
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): PartitionedResults {
  resolveWindowSchedule(config);

  const historyFloorKey = resultsHistoryFloorDayKey(nowUtc);
  const focusTurn = getResultsFocusTurn(config, nowUtc);
  const focusKey = focusTurn ? turnKey(focusTurn) : null;
  const official = officialResultsForHouse(results, drawSessions ?? [], nowUtc);
  const focusResult = focusTurn ? findResultForFocus(official, focusTurn) : null;

  const liveMap = new Map<string, {
    turn: PlayTurn;
    result: OfficialDrawResult | null;
    liveState: ResultTurnGroup['liveState'];
  }>();
  const historyMap = new Map<string, {
    turn: PlayTurn;
    result: OfficialDrawResult | null;
    liveState: ResultTurnGroup['liveState'];
  }>();
  const archivedMap = new Map<string, {
    turn: PlayTurn;
    result: OfficialDrawResult | null;
    liveState: ResultTurnGroup['liveState'];
  }>();

  if (focusTurn && focusKey) {
    for (const window of ['day', 'night'] as const) {
      const turn = { dayKey: focusTurn.dayKey, window };
      const key = turnKey(turn);
      const exact = findExactOfficial(official, turn.dayKey, window);
      const result = window === focusTurn.window
        ? (focusResult ?? exact)
        : exact;
      let liveState: ResultTurnGroup['liveState'] = 'past';
      if (window === focusTurn.window) liveState = 'active';
      else if (focusTurn.window === 'day' && window === 'night') liveState = 'upcoming';
      liveMap.set(key, { turn, result, liveState });
    }
  }

  for (const result of official) {
    const turn = { dayKey: result.dayKey, window: result.window };
    const key = turnKey(turn);
    if (liveMap.has(key)) continue;
    const entry = { turn, result, liveState: 'past' as const };
    if (result.dayKey >= historyFloorKey) {
      historyMap.set(key, entry);
    } else {
      archivedMap.set(key, entry);
    }
  }

  return {
    currentTurn: focusTurn,
    todayGroups: toGroups(liveMap, { chronologicalToday: true }),
    historyGroups: toGroups(historyMap),
    archivedGroups: toGroups(archivedMap),
  };
}
